/**
 * Showcase Component
 * Displays Item of the Day and Staff Picks from Remote Config
 */

'use client';

import { useEffect, useState } from 'react';
import { getRemoteConfigValue } from '@/lib/firebase.client';
import { ShowcaseItem } from '@/lib/validators';
import Image from 'next/image';

export default function Showcase() {
  const [itemOfTheDay, setItemOfTheDay] = useState<ShowcaseItem | null>(null);
  const [staffPicks, setStaffPicks] = useState<ShowcaseItem[]>([]);

  useEffect(() => {
    try {
      // Get Item of the Day
      const itemData = getRemoteConfigValue('item_of_the_day');
      if (itemData) {
        const item = JSON.parse(itemData) as ShowcaseItem;
        setItemOfTheDay(item);
      }

      // Get Staff Picks
      const picksData = getRemoteConfigValue('staff_picks');
      if (picksData) {
        const picks = JSON.parse(picksData) as ShowcaseItem[];
        setStaffPicks(picks);
      }
    } catch (error) {
      console.error('Error loading showcase data:', error);
    }
  }, []);

  const getTagClass = (tag: string) => {
    return `tag-${tag.toLowerCase().replace('_', '-')}`;
  };

  return (
    <div className="space-y-12">
      {/* Item of the Day */}
      {itemOfTheDay && (
        <section className="animate-fade-in">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            🌟 Item of the Day
          </h2>
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-3xl shadow-xl overflow-hidden max-w-2xl mx-auto">
            <div className="p-8">
              <div className="flex flex-col md:flex-row gap-6">
                {itemOfTheDay.image && (
                  <div className="relative w-full md:w-48 h-48 flex-shrink-0 rounded-2xl overflow-hidden bg-white">
                    <Image
                      src={itemOfTheDay.image}
                      alt={itemOfTheDay.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {itemOfTheDay.title}
                  </h3>
                  {itemOfTheDay.category && (
                    <p className="text-sm text-primary-700 font-medium mb-3">
                      {itemOfTheDay.category}
                    </p>
                  )}
                  <p className="text-gray-700 mb-4">{itemOfTheDay.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {itemOfTheDay.tags?.map((tag) => (
                      <span key={tag} className={getTagClass(tag)}>
                        {tag.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                  <a
                    href={itemOfTheDay.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="inline-block bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
                  >
                    Learn More →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Staff Picks */}
      {staffPicks.length > 0 && (
        <section className="animate-fade-in">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Staff Picks
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {staffPicks.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden card-hover"
              >
                {item.image && (
                  <div className="relative w-full h-48 bg-gray-100">
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  {item.category && (
                    <p className="text-sm text-primary-600 font-medium mb-3">
                      {item.category}
                    </p>
                  )}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {item.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {item.tags?.map((tag) => (
                      <span key={tag} className={getTagClass(tag)}>
                        {tag.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 font-semibold"
                  >
                    View Details →
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

